// npm libraries
import Vue from 'vue'
import Tooltip from 'vue-directive-tooltip'
import Notifications from 'vue-notification'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import { cloneDeep, isEqual, sortBy } from 'lodash'
import _debounce from 'lodash/debounce'
import { mapGetters, mapState } from 'vuex'

// Plugins
import { EventTracking } from '../../plugins/EventTracking'

// API
import DocumentApi from '../../apis/DocumentApi'
import UserApi from '../../apis/UserApi'

// Components
import LogoLoader from '../../components/logos/LogoLoader.vue'
import DocumentsPage from '../../documents/pages.vue'
import Bingo from '../../documents/bingo/bingo.vue'
import Flashcard from '../../documents/flashcard/flashcard.vue'

// Libraries
import Errors from '../../libraries/errors'
import Lang from '../../libraries/Lang'

// Helpers
import wmAlert from '../../helpers/wmAlerts'
import { hasNonIconImages } from '../../helpers/jsonObjectHelper'
import { dangerToast } from '../../helpers/toasts'

// Repositories
import { Fonts } from '../../repos/Fonts'
import { Modals } from '../../repos/Modals'

// Store
import store from '../../store/document'

// Mixins
import WithBrand from '../../mixins/WithBrand'
import WithPageViewTracking from '../../mixins/WithPageViewTracking'
import Common from '../../mixins/Common'
import EditPayWall from '../../mixins/EditPayWall'
import StatsigHelper from '../../mixins/StatsigHelper'
import PushListener from '../../mixins/PushListener'
import TopnavUtil from '../../mixins/TopnavUtil'
import LogInWatcher from '../../mixins/LogInWatcher'
import DocumentResizeListener from '../../mixins/DocumentResizeListener'
import WithGrooveHq from '../../mixins/WithGrooveHq'
import WithCustomScrollbar from '../../mixins/WithCustomScrollbar'

// Events
import UserEvent from '../../objects/UserEvent'

// Modals
import SubscriptionSuspended from '../../components/modals/SubscriptionSuspended.vue'

Vue.use(EventTracking, store)

Vue.use(Tooltip)
Vue.use(Notifications)

//bootstrap components
Vue.use(BootstrapVue)
Vue.use(BootstrapVueIcons)

Vue.component('SubscriptionSuspended', SubscriptionSuspended)

//set up some Vue values
Vue.prototype.$lang = new Lang()

//repos
Vue.prototype.$fonts = Fonts
Vue.prototype.$modals = Modals

Sentry.init({
    Vue,
    dsn: window.sentryDSN,
    integrations: [
        new BrowserTracing({
            tracingOrigins: [window.sentryTracingOrigin, /^\//],
        }),
        new Sentry.Replay({
            networkCaptureBodies: true,
            networkDetailAllowUrls: [window.location.origin],
        }),
    ],
    logErrors: true,
    tracesSampleRate: window.sentryTraceSampleRate,
    replaysOnErrorSampleRate: 1.0,
})

new Vue({
    store,
    el: '#app',
    components: {
        LogoLoader,

        // Widgets
        ThreeDots: () => import('../../widgets/ThreeDots.vue'),
        PremiumMarker: () => import('../../widgets/premium-marker.vue'),

        // Subscription/auth
        Login: () => import('../../components/Login.vue'),

        // Social
        ShareThisWorksheet: () => import('../../widgets/ShareThisWorksheet.vue'),

        // Modals
        Canny: () => import('../../components/Canny.vue'),
        CheckoutModal: () => import('../../components/modals/CheckoutModal.vue'),
        ErrorHandlingModal: () => import('../../components/ErrorHandlingModal.vue'),
        OnboardingModal: () => import('../../components/OnboardingModal.vue'),
        OneClickSuccess: () => import('../../components/OneClickSuccessModal.vue'),
        OnboardingComplete: () => import('../../components/OnboardingComplete.vue'),
        CancelSubscription: () => import('../../components/modals/CancelSubscription.vue'),

        // Documents page
        DocumentsPage,

        // Bingo
        Bingo,

        // Flashcard
        Flashcard,
        PageSetup: () => import('../../components/widgets/PageSetup.vue'),

        // Icons
        iconMagnifyPlus: () => import('../../stories/components/icons/IconMagnifyPlus.vue'),
        iconPdf: () => import('../../icons/icon-pdf.vue'),

        Paywall: () => import('../../components/Paywall.vue'),
        MaintenanceAlert: () => import('../../components/MaintenanceAlert.vue'),
    },
    mixins: [
        WithBrand,
        WithPageViewTracking,
        EditPayWall,
        StatsigHelper,
        Common,
        TopnavUtil,
        PushListener,
        LogInWatcher,
        DocumentResizeListener,
        WithGrooveHq,
        WithCustomScrollbar,
    ],
    data: {
        errors: new Errors(),
        userRating: null,
        ratingNeedsSaving: false,
        show_unpublish: false,
        isPrivate: false,
        pageSetupOpen: false,
        isLoading: false,
        mediumWidth: 1304,
        workspaceWidth: 730,
        resizedUp: false,
        currentWidth: window.innerWidth,
        previousWidth: window.innerWidth,
        allowRescale: false,
        originalPageSetup: null,
        generating: false,
    },
    computed: {
        isFirefox() {
            return window.navigator.userAgent.match(/Firefox\/([0-9]+)\./)
        },
        screenSizeTooSmall() {
            return this.currentWidth < 825
        },
        eventMessage() {
            if (!this.isLoggedIn) {
                return 'not_signed_in'
            }

            return this.document.user_id === this.user.user.id ? 'creator' : 'non_creator'
        },
        ...mapGetters({
            isLoggedIn: 'user/isLoggedIn',
            documentTypes: 'document/allDocumentTypes',
            documentRequiresPagination: 'document/documentRequiresPagination',
            zoomLevels: 'document/getZoomLevels',
            hasPlans: 'subscription/hasPlans',
            worksheet: 'document/worksheet',
            isBingo: 'document/isBingo',
            isFlashcard: 'document/isFlashcard',
            isWorksheet: 'document/isWorksheet',
            answerable: 'document/answerable',
            documentModel: 'document/documentModel',
            flashcard: 'document/flashcard',
            worksheetImages: 'document/worksheetAllImages',
            flashcardImages: 'document/flashcardAllImages',
            bingoImages: 'document/bingoAllImages',
            entityType: 'document/entityType',
            persistAction: 'document/immediatePersistAction',
            style: 'document/documentStyle',
            worksheetInlineImages: 'document/worksheetInlineImages',
            isHeaderSticky: 'abtests/stickyHeader',
            showSuspendedNotice: 'subscription/showSuspendedNotice',
        }),
        ...mapState(['user', 'document']),
        images() {
            if (this.entityType === 'worksheet') return this.worksheetImages
            if (this.entityType === 'flashcard') return this.flashcardImages
            if (this.entityType === 'bingo') return this.bingoImages
            return []
        },
        premiumImages() {
            // should return personal images and premium iconfinder images
            // todo: this should be updated after iconfinder implemented
            return this.images
        },
        hasPremiumImages() {
            return this.premiumImages.length > 0 && hasNonIconImages(this.premiumImages)
        },
        documentItem() {
            return window.doc ? window.doc : this.documentModel
        },
        showAnswerOption() {
            return this.answerable && !['bingo', 'flashcard'].includes(this.document.entity_type)
        },
        pdf_key() {
            let key = 'pdf'

            if (this.answer_key) {
                key += '_answer_key'
            }

            if (this.ink_saver) {
                key += '_ink_saver'
            }

            if (this.user.user.white_label) {
                key += '_white_label'
            }

            return key
        },
        pdf_url() {
            return this.document.media.pdfs[this.pdf_key]
        },
        mediaLoaded() {
            for (let key in this.document.media.pdfs) {
                if (this.document.media.pdfs[key] == '') {
                    return false
                }
            }
            return true
        },
        answer_key: {
            get() {
                return this.isBingo || this.isFlashcard ? 0 : this.document.show_answer_key
            },
            async set(value) {
                await this.$store.dispatch('document/setDocument', {
                    show_answer_key: value,
                })
            },
        },
        ink_saver: {
            get() {
                return this.user.user.ink_saver
            },
            async set(value) {
                await this.$store.dispatch('user/updateUser', { ink_saver: value })
            },
        },
        loadCompleted() {
            return {
                type: this.worksheet.type,
                fonts: this.$fonts.ready,
            }
        },
        ratingCount() {
            let s = 's' //plural
            if (this.document.rating_count == 1) {
                s = ''
            }
            return this.document.rating_count + ' rating' + s
        },
        belongsToUser() {
            if (this.document.user_id == this.user.user.id) {
                return true
            }
            return false
        },
        pageSetupData() {
            return {
                page_setup: this.flashcard.page_setup,
                flashcard_item_height: parseFloat(this.flashcard.flashcard_item_height),
                style: this.style,
            }
        },
        shouldGeneratePdf() {
            if (this.ink_saver || this.answer_key) return true

            if (this.isFlashcard) return !isEqual(sortBy(this.pageSetupData), sortBy(this.originalPageSetup))

            return false
        },
        hasWorksheetInlineImages() {
            return this.isWorksheet && this.worksheetInlineImages.length > 0
        },
        isDocumentOwner() {
            return this.user.user.id == this.document.user_id && this.isLoggedIn
        },
        zoomContainerClass() {
            return {
                'zoom-container-sticky': this.isHeaderSticky,
            }
        },
    },
    watch: {
        isLoggedIn: function (newValue) {
            if (newValue && this.userRating !== null && this.ratingNeedsSaving) {
                //user logged in
                this.setRating()
            }
        },
        loadCompleted: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    if (this.loadCompleted.fonts) {
                        this.$store.dispatch('document/paginateItems')
                    }
                })
            },
        },
        documentRequiresPagination: {
            // repaginate anytime the worksheet changes.
            deep: true,
            handler() {
                this.$nextTick(() => {
                    if (this.loadCompleted.fonts) {
                        this.$store.dispatch('document/paginateItems')
                    }
                })
            },
        },
        pageSetupData: {
            deep: true,
            handler(newValue, oldValue) {
                if (this.isFlashcard && oldValue.style !== undefined) {
                    this.saveOwnerChanges()
                }
                this.$nextTick(() => {
                    this.$store.dispatch('document/adjustZoomContainer')
                })
            },
        },
        showSuspendedNotice(newVal) {
            if (newVal) {
                this.$bvModal.show('subscription-suspended')
            }
        },
    },
    beforeCreate() {
        this.$store.dispatch('user/initialize')
        this.$store.dispatch('abtests/initialize')
    },
    async created() {
        window.addEventListener('keydown', this.printHandler)

        if (window.doc) {
            await this.$store.dispatch('document/setWindowDocument', window.doc)

            await this.$store.dispatch('document/setDocument', {
                edited: window.doc.edited,
            })
        }

        await this.$store.dispatch('document/setVueInstance', this)

        //if we have a user waiting for us load in that user to the store
        if (window.user?.id) {
            await this.$store.dispatch('user/setUser', window.user)
            if (window.user.ratings !== undefined && window.user.ratings.length > 0) {
                this.userRating = window.user.ratings[0].rating
            } else {
                this.userRating = 0
            }
        }

        // Set show_answer_key to false on the published document page.
        this.answer_key = 0

        if (this.isLoggedIn && window.doc.user_id == window.user.id) {
            await this.$store.dispatch('document/fetchDocumentPurchaseStatus')
        }

        if (!this.mediaLoaded && !window.isQueueWorker) {
            let mediainterval = setInterval(async () => {
                await DocumentApi.get(this.document.entity_type, this.document.id)
                    .then(async (result) => {
                        let documentMedia = {}
                        if (result.data.pdfs) {
                            documentMedia.pdfs = result.data.pdfs
                        }
                        if (result.data.image) {
                            documentMedia.image = result.data.image
                        }
                        if (result.data.thumb) {
                            documentMedia.thumb = result.data.thumb
                        }
                        if (result.data.bordered_image) {
                            documentMedia.bordered_image = result.data.bordered_image
                        }
                        if (result.data.medias) {
                            await this.$store.dispatch('document/setDocument', { medias: result.data.medias })
                        }

                        //update the worksheet
                        await this.$store.dispatch('document/setDocumentMedia', documentMedia)

                        if (this.mediaLoaded) {
                            //no longer need to poll the server
                            clearInterval(mediainterval)
                        }
                    })
                    .catch(async (error) => {
                        await this.$store.dispatch(
                            'document/setApiCallFeedback',
                            'There was a problem loading this document.',
                        )
                        await this.$modals.open('error_handling')
                    })
            }, 3000)
        }
    },
    mounted() {
        //add pinterest JS library
        let pinterest = document.createElement('script')
        pinterest.setAttribute('src', '//assets.pinterest.com/js/pinit.js') //generate partial url
        pinterest.setAttribute('async', true)
        document.getElementsByTagName('head')[0].appendChild(pinterest)

        //add twitter JS library
        let twitter = document.createElement('script')
        twitter.setAttribute('src', 'https://platform.twitter.com/widgets.js') //generate partial url
        twitter.setAttribute('charset', 'utf-8')
        document.getElementsByTagName('head')[0].appendChild(twitter)

        window.onresize = (event) => {
            this.resizeDocument(event)
            this.updateResizeDirection()
        }

        window.document.addEventListener('DOMContentLoaded', (event) => {
            this.resizeDocument(event)
        })

        this.$store.dispatch('document/adjustZoomContainer')
        this.$store.dispatch('document/paginateItems')
        this.$store.dispatch('document/scaleDocument', true)

        this.originalPageSetup = cloneDeep({
            page_setup: this.flashcard.page_setup,
            flashcard_item_height: this.flashcard.flashcard_item_height,
            style: this.style,
        })

        if (this.showSuspendedNotice) {
            this.$bvModal.show('subscription-suspended')
        }
    },
    methods: {
        updateResizeDirection() {
            this.currentWidth = window.innerWidth

            if (this.currentWidth > this.previousWidth) {
                this.resizedUp = true
            } else if (this.currentWidth < this.previousWidth) {
                this.resizedUp = false
            }

            this.previousWidth = this.currentWidth
        },
        setZoom(size) {
            this.$store.dispatch('document/setZoom', size)
            this.$store.dispatch('document/scaleDocument', true)
        },
        isDocumentHidden(selectorId, containerId) {
            const selectedElement = document.getElementById(selectorId)
            const selectedElementBoundingRect = selectedElement.getBoundingClientRect()

            const viewportWidth = window.innerWidth || document.documentElement.clientWidth

            let selectedElementRightBoundary = selectedElementBoundingRect.right

            if (containerId) {
                let containerElement = document.getElementById(containerId)
                let containerStyles = window.getComputedStyle(containerElement)
                let rightPadding = parseFloat(containerStyles.getPropertyValue('padding-right'))

                selectedElementRightBoundary += rightPadding
            }

            return selectedElementRightBoundary > viewportWidth
        },
        hasFeatureShowPage(feature, document, docType) {
            if (this.isLoggedIn) {
                return this.hasFeature(feature, document, docType)
            } else {
                return false
            }
        },
        async printHandler(e) {
            if (!e.repeat && ((e.metaKey && e.keyCode == 80) || (e.ctrlKey && e.keyCode == 80))) {
                await this.$logTrackingEvent(UserEvent.PRINT_SHORTCUT, this.eventMessage)
            }
        },
        async downloadPDF() {
            await this.$logTrackingEvent(UserEvent.PDF_DOWNLOAD, this.eventMessage)
            if (this.shouldGeneratePdf) {
                this.generating = true
                let payload = {
                    document_id: this.document.id,
                    white_label: this.user.user.white_label,
                    ink_saver: this.ink_saver,
                    answer_key: this.answer_key,
                }
                if (this.isFlashcard) {
                    payload.details = this.pageSetupData
                }
                this.$store
                    .dispatch('document/generatePdf', payload)
                    .then((response) => {
                        fetch(`data:application/pdf;base64,${response.data.base64pdf}`)
                            .then((base64Response) => base64Response.blob())
                            .then((blobResponse) => {
                                const blob = new Blob([blobResponse], { type: 'application/pdf' })
                                const blobUrl = URL.createObjectURL(blob)
                                this.newTab(blobUrl, false, response.data.filename)
                            })
                    })
                    .catch((error) => {
                        this.$bvToast.toast('There was an error generating the PDF. Please try again.', dangerToast)
                        console.log('PDF ERROR: ', error)
                    })
                    .finally(() => {
                        this.generating = false
                    })
            } else {
                if (this.pdf_url) this.newTab(`/documents/${this.document.id}/pdf-url?key=${this.pdf_key}`)
            }
        },
        async goPremium() {
            await this.$store.dispatch('document/setGoPremium')

            if (this.isLoggedIn) {
                await this.showPremiumPaywall()
            } else {
                await this.$modals.open('logIn')
            }
        },
        setWhiteLabel(value) {
            this.isLoading = true
            this.$store
                .dispatch('user/updateUser', { white_label: value })
                .then(() => {
                    this.$store
                        .dispatch('user/setWhiteLabel', value)
                        .then(() => {
                            this.isLoading = false
                        })
                        .catch((error) => {
                            this.isLoading = false
                        })
                })
                .catch((error) => {
                    this.isLoading = false
                })
        },
        updateUserValues() {
            this.$store.dispatch('user/updateUser', {})
            if (this.isLoggedIn) {
                UserApi.update(this.user.user.id, {
                    white_label: this.user.user.white_label,
                    ink_saver: this.user.user.ink_saver,
                }).then((response) => {
                    // do nothing from here. We're just updating for later.
                })
            }
        },
        async print() {
            await this.$logTrackingEvent(UserEvent.PRINT, this.eventMessage)
            window.print()
        },
        floatStars(ev) {
            let parent = ev.target.parentElement
            let children = parent.children
            let showBorder = false
            for (let i = 0; i < children.length; i++) {
                if (!showBorder) {
                    children[i].classList.add('rating-hover-solid')
                } else {
                    children[i].classList.add('rating-hover-border')
                }

                if (children[i] == ev.target) {
                    showBorder = true
                }
            }
        },
        unfloatStars(ev) {
            let children = ev.target.parentElement.children
            for (let i = 0; i <= children.length; i++) {
                if (children[i]) {
                    children[i].classList.remove('rating-hover-solid')
                    children[i].classList.remove('rating-hover-border')
                }
            }
        },
        starTest(star) {
            let avg_rating = parseFloat(this.document.avg_rating ?? 0)

            //if the worksheet belongs to this user default to 0
            if (this.belongsToUser) {
                avg_rating = 0
            }

            //if the user has rated this worksheet override the avg_rating with the users rating
            if (this.userRating) {
                avg_rating = this.userRating
            }

            if (star <= avg_rating) {
                return 'star-fill'
            } else if (star == Math.ceil(avg_rating)) {
                return 'star-half'
            } else {
                return 'star'
            }
        },
        async activateRating(rating) {
            this.userRating = rating
            this.ratingNeedsSaving = true

            if (this.isLoggedIn) {
                this.setRating()
            } else {
                this.$modals.open('logIn')
                this.$modals.logIn.active_tab = 'log_in'
                await this.$logTrackingEvent(UserEvent.LOGIN_VIEW_WORKSHEET_RATING, this.eventMessage)
            }
        },
        setRating(rating) {
            DocumentApi.rate(this.document.entity_type, this.document.id, this.userRating).catch(() => {
                //failed
                this.$store.dispatch('document/setApiCallFeedback', 'There was an error saving your rating.')
                this.$modals.open('error_handling')
            })
        },
        onDeleteDocument() {
            wmAlert
                .confirm({
                    html: 'This will permanently delete your document and cannot be undone.',
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.deleteDocument()
                    }
                })
        },
        deleteDocument() {
            DocumentApi.delete(this.document.entity_type, this.document.id)
                .then((response) => {
                    window.location = '/user/my-documents'
                })
                .catch((error) => {
                    this.$store.dispatch('document/setApiCallFeedback', 'There was a problem deleting this document.')
                    this.$modals.open('error_handling')
                })
        },
        unpublishDocument() {
            window.location = `/${this.document.entity_type}/${this.document.id}/unpublish`
        },
        updatePrivacy() {
            this.$store.dispatch('document/requestUpdateDocument')
            this.$store.dispatch(this.persistAction)
        },
        setPageSetupData(updated) {
            this.$store.dispatch('document/setFlashcard', {
                ...updated,
            })
        },
        pageSetupOpenClose() {
            this.pageSetupOpen = !this.pageSetupOpen
        },
        saveOwnerChanges: _debounce(function () {
            if (window?.flashcard_type && window?.flashcard_type !== this.flashcard.page_setup.flashcard_type) return

            if (this.isLoggedIn && this.user.user.id == this.document.user_id) {
                this.$store.dispatch('document/updateDocument', true)
            }
        }, 1500),
        copyDocument() {
            const elem = document.querySelector('#copy-document')
            let url = elem.dataset.url

            if (this.isFlashcard && this.shouldGeneratePdf) {
                url = `${url}?details=${btoa(JSON.stringify(this.pageSetupData))}`
            }
            window.location = url
        },
        async handleRemoveLogoGoPremium() {
            await this.$logTrackingEvent(UserEvent.PREMIUM_MARKER_CLICKED, 'remove-logo')
            this.goPremium()
        },
    },
})
